import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Copy, Gift, Check, Twitter, Linkedin, MessageCircle, Mail, Loader2, Send } from "lucide-react";

/**
 * ReferralModal — Refer-a-friend ONLY (the Invite-team tab was removed
 * per product call). Triggered by the sidebar "Refer to earn credits" link.
 *
 * Backend:
 *   GET /api/me/referral → { referral_code, referred_count, credits_earned, credits_per_referral }
 */
export default function ReferralModal({ open, onClose }) {
  const { user } = useAuth();
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  // Email-invite form state. Lives alongside the link-share UI but is
  // entirely independent — the user picks whichever channel they prefer.
  const [emailInput, setEmailInput] = useState("");
  const [customMsg, setCustomMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [lastInviteResult, setLastInviteResult] = useState(null);

  const fetchReferral = useCallback(async () => {
    if (!open) return;
    try {
      const r = await axios.get(`${API}/me/referral`, { withCredentials: true });
      setReferral(r.data);
    } catch {
      // 401 silently no-ops; interceptor handles 5xx
    }
  }, [open]);

  useEffect(() => { fetchReferral(); }, [fetchReferral]);

  // Wokelo-branded short link domain. Falls back to the current origin so
  // local/preview environments still produce a working link.
  const BRAND_HOST = "https://slides.wokelo.ai";
  const referUrl = referral
    ? `${BRAND_HOST}/signup?ref=${referral.referral_code}`
    : "";

  const handleCopy = async () => {
    if (!referUrl) return;
    try {
      await navigator.clipboard.writeText(referUrl);
      setCopied(true);
      toast.success("Link copied — share away!");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Select manually and copy.");
    }
  };

  // Parse the email input into a clean, deduped list. Accepts comma,
  // semicolon, newline, OR space separation so the field is forgiving for
  // pasted address books and "manually typed list" both.
  const parseEmails = (raw) => {
    return Array.from(new Set(
      (raw || "").split(/[,;\n\s]+/).map(e => e.trim().toLowerCase()).filter(Boolean)
    ));
  };
  const parsedEmails = parseEmails(emailInput);
  const validEmails = parsedEmails.filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  const invalidEmails = parsedEmails.filter(e => !validEmails.includes(e));

  const handleSendInvites = async () => {
    if (!validEmails.length) {
      toast.error("Add at least one valid email address.");
      return;
    }
    setSending(true);
    setLastInviteResult(null);
    try {
      const r = await axios.post(`${API}/me/referral/invite`, {
        emails: validEmails,
        custom_message: customMsg.trim() || null,
      }, { withCredentials: true });
      setLastInviteResult(r.data);
      const sent = r.data.total_sent || 0;
      const skipped = r.data.total_skipped || 0;
      const skippedList = r.data.skipped || [];
      // `sent` only counts addresses we *attempted* — the actual delivery
      // verdict lives on each item's `delivered` flag. Mandrill's global
      // block list can reject test addresses or repeat-spam targets and
      // return `delivered:false` even though we POST'd successfully.
      // Surface that honestly so users don't think their friend got an
      // email that the provider silently dropped.
      const delivered = (r.data.sent || []).filter(x => x.delivered).length;
      const dropped = sent - delivered;

      // Carve out the "already on Wokelo" subset so we can show a
      // friendly per-address toast — much better UX than a vague
      // "skipped 1" count, and exactly matches what users asked for.
      const alreadyJoined = skippedList.filter(x => x.reason === "already_signed_up");
      if (alreadyJoined.length === 1) {
        toast.info(
          `Looks like ${alreadyJoined[0].email} is already on Wokelo — but you can still refer other friends!`,
          { duration: 6000 }
        );
      } else if (alreadyJoined.length > 1) {
        toast.info(
          `${alreadyJoined.length} of your invitees are already on Wokelo — refer new friends to earn credits.`,
          { duration: 6000 }
        );
      }

      if (delivered) {
        toast.success(`Invitation${delivered > 1 ? "s" : ""} sent to ${delivered} friend${delivered > 1 ? "s" : ""}!`);
      } else if (dropped) {
        toast.error(`Couldn't deliver ${dropped} invite${dropped > 1 ? "s" : ""} — check the addresses and try again.`);
      } else if (!alreadyJoined.length && skipped) {
        toast.error("No invites sent — all addresses skipped.");
      }
      setEmailInput("");
      setCustomMsg("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invites.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      {/* No relative positioning + no manual close button — Radix renders its own X.
          Adding our own causes the duplicate-X bug we just fixed. */}
      <DialogContent
        className="sm:max-w-[600px] w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0 gap-0"
        data-testid="referral-modal"
      >
        <DialogHeader className="p-0">
          <DialogTitle className="sr-only">Refer to earn credits</DialogTitle>
        </DialogHeader>

        {/* Header banner */}
        <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-teal-50 via-white to-amber-50 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 tracking-tight">
                Refer to earn credits
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Share Slides by Wokelo with friends.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5" data-testid="refer-pane">
          {/* Pitch */}
          <div className="text-sm text-gray-700 leading-relaxed">
            Give{" "}
            <span className="font-bold text-teal-700">
              {referral?.credits_per_referral ?? 50} credits
            </span>
            {" "}and earn{" "}
            <span className="font-bold text-teal-700">
              {referral?.credits_per_referral ?? 50} credits
            </span>
            {" "}for each new friend who signs up using your link.
          </div>

          {/* Link — primary CTA, intentionally placed above stats so users
              can copy/share in one motion. Larger type + softly highlighted
              container makes it the visual focal point of the modal. */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-teal-700 mb-2">
              Your referral link
            </p>
            <div className="flex flex-col sm:flex-row sm:items-stretch border-2 border-teal-200 rounded-xl overflow-hidden bg-gradient-to-br from-teal-50/60 to-white shadow-sm">
              <div className="flex-1 px-4 py-3.5 text-sm sm:text-base text-gray-900 truncate font-mono font-medium" data-testid="referral-link-display">
                {referUrl || "Loading…"}
              </div>
              <button
                onClick={handleCopy}
                disabled={!referUrl}
                className={`flex-shrink-0 px-5 py-3 sm:py-0 flex items-center justify-center gap-1.5 text-sm font-semibold sm:border-l-2 border-t-2 sm:border-t-0 border-teal-200 transition-colors whitespace-nowrap ${
                  copied ? "bg-emerald-600 text-white" : "bg-teal-600 hover:bg-teal-700 text-white"
                }`}
                data-testid="referral-copy-btn"
              >
                {copied ? <><Check className="w-4 h-4" />Copied</> : <><Copy className="w-4 h-4" />Copy</>}
              </button>
            </div>

            {/* One-click share — pre-fills a friendly invite copy across viral
                channels. Each platform opens in a new tab so users don't lose
                context with the modal still open behind. */}
            {(() => {
              const message = `I'm using Slides by Wokelo to whip up presentations in seconds. Try it — we both get ${referral?.credits_per_referral ?? 50} free credits:`;
              const shareUrl = referUrl || BRAND_HOST;
              const fullText = `${message} ${shareUrl}`;
              const links = [
                {
                  label: "X / Twitter", icon: Twitter, testId: "share-twitter",
                  href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(fullText)}`,
                },
                {
                  label: "LinkedIn", icon: Linkedin, testId: "share-linkedin",
                  href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
                },
                {
                  label: "WhatsApp", icon: MessageCircle, testId: "share-whatsapp",
                  href: `https://wa.me/?text=${encodeURIComponent(fullText)}`,
                },
              ];
              return (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="referral-share-row">
                  <span className="text-[11px] uppercase tracking-widest font-semibold text-gray-400">Share via</span>
                  {links.map(({ label, icon: Icon, href, testId }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={label}
                      data-testid={testId}
                      className="w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-500 flex items-center justify-center hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-colors"
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* ─── Send invites by email ─── */}
          <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="referral-email-invite">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-teal-600" />
              <p className="text-sm font-semibold text-gray-900">Send by email</p>
              <span className="text-[10px] uppercase tracking-widest text-gray-400">we'll send the invite</span>
            </div>
            <textarea
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="friend@example.com, colleague@company.com&#10;another@example.com"
              rows={2}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 placeholder:text-gray-400 resize-none"
              data-testid="referral-emails-input"
              disabled={sending}
            />
            <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-400 min-h-[16px]">
              <span>
                {validEmails.length > 0 && <span className="text-teal-600 font-medium">{validEmails.length} valid · </span>}
                {invalidEmails.length > 0 && <span className="text-amber-600">{invalidEmails.length} invalid</span>}
              </span>
              <span>Comma, space, or newline separated</span>
            </div>
            <textarea
              value={customMsg}
              onChange={(e) => setCustomMsg(e.target.value)}
              placeholder="Optional personal note (e.g., 'Thought you'd love this')"
              rows={2}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md mt-2 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 placeholder:text-gray-400 resize-none"
              data-testid="referral-custom-msg"
              maxLength={600}
              disabled={sending}
            />
            <div className="flex items-center justify-between mt-3">
              <p className="text-[11px] text-gray-500">
                We'll send the invite from <strong>no-reply@wokelo.ai</strong> with your unique link and your note (if any). Your inbox isn't used.
              </p>
              <button
                onClick={handleSendInvites}
                disabled={sending || !validEmails.length}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="referral-send-invites-btn"
              >
                {sending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Sending</> : <><Send className="w-3.5 h-3.5" />Send invites</>}
              </button>
            </div>
            {lastInviteResult && (
              <div className="mt-3 text-[11px] space-y-0.5 text-gray-500" data-testid="referral-invite-result">
                {lastInviteResult.total_sent > 0 && (
                  <p className="text-teal-700"><Check className="inline w-3 h-3 mr-1" />Sent to {lastInviteResult.total_sent} friend{lastInviteResult.total_sent > 1 ? "s" : ""}</p>
                )}
                {lastInviteResult.skipped?.map((s, i) => (
                  <p key={i} className="text-gray-400">
                    Skipped <span className="font-mono">{s.email}</span> — {s.reason === "already_signed_up" ? "already on Wokelo" : s.reason === "self" ? "that's you!" : "recently invited"}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Stats — moved below the link so the share action is the
              primary visual focus, with progress as a secondary signal. */}
          {referral && (
            <div className="grid grid-cols-2 gap-3" data-testid="referral-stats">
              <Stat label="Friends referred" value={referral.referred_count} />
              <Stat label="Credits earned" value={referral.credits_earned} accent="teal" />
            </div>
          )}

          {/* FAQ */}
          <div className="pt-2 border-t border-gray-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">FAQ</p>
            <details className="group py-1.5 border-b border-gray-100">
              <summary className="text-sm font-medium text-gray-800 cursor-pointer hover:text-gray-900">
                When do I get credits?
              </summary>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                As soon as your friend signs up using your unique link, both of you get the credits added instantly.
              </p>
            </details>
            <details className="group py-1.5 border-b border-gray-100">
              <summary className="text-sm font-medium text-gray-800 cursor-pointer hover:text-gray-900">
                Is there a cap?
              </summary>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                No cap today — refer as many friends as you'd like. We may revisit if abuse becomes an issue.
              </p>
            </details>
            <details className="group py-1.5">
              <summary className="text-sm font-medium text-gray-800 cursor-pointer hover:text-gray-900">
                What counts as a successful referral?
              </summary>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                A signup with a verified email through your link. Self-referrals don't count.
              </p>
            </details>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <div className={`text-2xl font-extrabold tracking-tight ${accent === "teal" ? "text-teal-700" : "text-gray-900"}`}>
        {value ?? 0}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}
